/**
 * StartExamModal Component
 * Modal for exam instructions and filters before starting exam
 */

import React, { useState, useRef } from 'react';
import { Modal, Select } from 'antd';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SegmentedExam from '../../../../../Exams/SegmentedExam';
import { candidateCategoryDDL, facultySubjectDDL } from '../../../../../../constants/staticPreferences';

/**
 * StartExamModal - Modal component for exam instructions
 * @param {Object} props
 * @param {boolean} props.visible - Modal visibility
 * @param {Function} props.onCancel - Callback when modal is cancelled
 * @param {Function} props.onOk - Callback when Start Exam is clicked
 * @param {Object} props.exam - Exam data object
 * @param {Array} props.allSubjects - Array of all subjects
 * @param {string} props.groupId - Group ID
 */
const StartExamModal = ({
  visible,
  onCancel,
  onOk,
  exam,
  allSubjects,
  groupId
}) => {
  const history = useHistory();
  const subjectSelectionRef = useRef();
  
  const segmentedExamSubjectSelectedSubmitted = useSelector(
    state => state.exams.segmentedExamSubjectSelectedSubmitted
  );

  const [faculty, setFaculty] = useState(null);
  const [subject, setSubject] = useState(null);
  const [candidateCategory, setCandidateCategory] = useState(null);
  const [subjectListDDL, setSubjectListDDL] = useState(null);

  // Get subject name from exam
  const getSubject = () => {
    // Check if subject_info is available in exam
    if (exam?.subject_info?.name) {
      return exam.subject_info.name;
    }
    // Fallback to checking allSubjects
    if (allSubjects && allSubjects.length > 0 && exam?.examId?.subjectId) {
      for (let i = 0; i < allSubjects.length; i++) {
        const element = allSubjects[i];
        if (exam?.examId?.subjectId === element?._id) {
          return element?.name;
        }
      }
    }
    return 'Not given';
  };

  // Clear exam start preferences
  const clearExamStartPreference = () => {
    localStorage.removeItem('exam_candidateCategory');
    localStorage.removeItem('exam_faculty');
    localStorage.removeItem('exam_subject');
    setFaculty(null);
    setSubject(null);
    setCandidateCategory(null);
  };

  // Handle modal cancel
  const handleCancel = () => {
    clearExamStartPreference();
    if (onCancel) {
      onCancel();
    }
  };

  // Handle Start Exam button click
  const handleOk = async () => {
    const subjectName = getSubject();

    // Save filters to localStorage
    if (candidateCategory) {
      localStorage.setItem('exam_candidateCategory', JSON.stringify(candidateCategory));
    } else {
      localStorage.removeItem('exam_candidateCategory');
    }

    if (faculty) {
      localStorage.setItem('exam_faculty', JSON.stringify(faculty));
    } else {
      localStorage.removeItem('exam_faculty');
    }

    if (subject) {
      localStorage.setItem('exam_subject', JSON.stringify(subject));
    } else {
      localStorage.removeItem('exam_subject');
    }

    // Handle segmented exam
    if (exam?.examId?.isSegmentedExam) {
      if (
        exam?.examId?.isPracticeExam ||
        (!exam?.examId?.isPracticeExam && !segmentedExamSubjectSelectedSubmitted)
      ) {
        try {
          const result = await subjectSelectionRef.current.submitSegmentedExamSubject();
          if (!result) {
            return;
          }
        } catch (error) {
          console.error('subjectSelectionRef', error);
          alert(error);
          return;
        }
      }
    }

    // Clear all previous exam state for fresh start
    localStorage.removeItem('localSavedAnswers');
    localStorage.removeItem('isLive');
    localStorage.removeItem('examMetaData');
    localStorage.removeItem('selectedSegmentedExamSubjects');
    
    // Save allSubjects to localStorage
    localStorage.setItem('allSubjects', JSON.stringify(allSubjects));
    localStorage.setItem('subjectOfSelectedExam', subjectName);

    // Navigate to exam page
    history.push(`/live-exam/${exam?.examId?._id}/${groupId}`);

    // Call onOk callback if provided
    if (onOk) {
      onOk();
    }
  };

  return (
    <Modal
      width={800}
      open={visible}
      maskClosable={false}
      okText="Start Exam"
      title="Exam Instruction"
      onCancel={handleCancel}
      onOk={handleOk}
      destroyOnClose
    >
      {/* Exam Instructions */}
      <div
        dangerouslySetInnerHTML={{
          __html: exam?.examId?.instruction || 'There is no instruction given for this exam'
        }}
      />

      {/* Segmented Exam Component */}
      {exam?.examId?.isSegmentedExam && (
        <SegmentedExam
          segmentedExamDetails={exam?.examId?.segmentedExamDetails}
          subjects={allSubjects}
          examId={exam?.examId?._id}
          visible={visible}
          isPracticeExam={exam?.examId?.isPracticeExam}
          ref={subjectSelectionRef}
        />
      )}

      {/* Filter Section */}
      <div className="exam-start-filter-section" style={{ marginTop: 16 }}>
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          allowClear
          value={candidateCategory}
          onChange={setCandidateCategory}
          placeholder="Candidate Category"
          options={candidateCategoryDDL}
        />
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          allowClear
          value={faculty}
          onChange={(v, option) => {
            setFaculty(v);
            setSubject(null);
            setSubjectListDDL(option?.subjects || null);
          }}
          placeholder="Select Faculty"
          options={facultySubjectDDL}
        />
        <Select
          style={{ width: '100%' }}
          allowClear
          value={subject}
          onChange={setSubject}
          placeholder="Select Subject"
          options={subjectListDDL}
        />
      </div>
    </Modal>
  );
};

export default StartExamModal;

